import React from 'react';
import { get } from 'lodash';
import searchQuery from 'query-string';
import { withRouter } from 'react-router-dom';

// React view component to render
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import {memberDetailsClaimsEntityWidget as MemberDetailsClaimsEntityWidgetView  } from 'ui/views/widgets/memberDetailsClaimsEntityWidget';
import { MemberDetailsClaimsEntityWidgetService } from 'services/memberDetailsClaimsEntityWidget';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as backRoute from 'utils/backRoute';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { OVERLAY_VIEWS } from 'constants/templates';
import { overlay } from 'modules/overlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsClaimsEntityWidget } from 'ui/containers/MemberDetailsClaimsEntityWidget';
 *
 * // render
 * <MemberDetailsClaimsEntityWidget propName='propValue'/>
 */
export class _MemberDetailsClaimsEntityWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // Member Detail instance
        this.memberDetailsClaimsEntityWidgetService = new MemberDetailsClaimsEntityWidgetService();

        // start member detail data fetch
        this.memberDetailsClaimsEntityWidgetData();
    }

    async memberDetailsClaimsEntityWidgetData() {
       
        log.debug( 'MemberDetailsClaimsEntityWidgetService.memberDetailsClaimsEntityWidgetData()' );

        // cancel ongoing request
        this.memberDetailsClaimsEntityWidgetService.cancel();

        try {
            const { status, data }  = await this.memberDetailsClaimsEntityWidgetService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    // function called when user click on to-do against a claim
    async initiateClaimReversal( { payload, action  } ) {
        log.debug( 'MemberDetailsClaimsEntityWidgetService.initiateClaimReversal()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberDetailsClaimsEntityWidgetService.cancel();

        try {
            const { status, data }  = await this.memberDetailsClaimsEntityWidgetService.fetchClaimReversalInfo( {
                url: action.url,
                data: payload,
            } );

            pageLevelPreloader.hide();

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                
                errorOverlay.open();

            } else {

                // open overlay and send the `data`
                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.CLAIM_TODO,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    config: {},
                    data: {
                        ...payload,
                        ...action,
                        ...data,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            }

        } catch( e ) {

            pageLevelPreloader.hide();
            
            errorOverlay.open();
        }
    }

    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {}, /*selection = [], parentItem = {}*/ } = {} ) {
        log.debug( 'MemberDetailsClaimsEntityWidgetService.handleActionClick()' );

        const memberId = get( this.props, 'requestPayload.context.memberId', null );
        const patientId = get( this.props, 'requestPayload.context.patientId', null );

        // prepare back-route state payload
        const backRouteState = backRoute.encode( {
            pathname: this.props.location.pathname,
            payload: {
                entitiesActiveTabIndex: this.props.tabIndex,
                memberId: memberId,
                patientId: patientId
            },
        } );

        // when `claimID` is clicked, redirect to member detail page
        if( 'column' === type && 'claimNumber' === value.name ) {
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.CLAIM_DETAIL,
                state: { ...backRouteState,  memberId: memberId, patientId: patientId },
                search: searchQuery.stringify( {
                    claimId: item.id,
                } )
            } );
        }

        // when `rxNumber` is clicked, redirect to rxDetail detail page
        if( 'column' === type && 'rxNumber' === value.name ) {
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.PRESCRIPTION_DETAIL,
                state: { ...backRouteState,  memberId: memberId, patientId: patientId },
                search: searchQuery.stringify( {
                    rxId: get( get( item, 'values', {} ), 'rxNumber' )
                } )
            } );
        }

        if( 'groupAction' === type && 'toDos' === value.name ) {
             this.initiateClaimReversal( {
                payload: {
                    context: {
                        memberId: memberId,
                        patientId: patientId,
                        claimId: item.id,
                    }
                },
                action: value
             } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberDetailsClaimsEntityWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberDetailsClaimsEntityWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                requestPayload={ this.props.requestPayload }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_MemberDetailsClaimsEntityWidget.displayName = 'MemberDetailsClaimsEntityWidget';

// set default props
_MemberDetailsClaimsEntityWidget.defaultProps = {
    requestPayload: {},
};

// prop types
_MemberDetailsClaimsEntityWidget.propTypes = {};

export const MemberDetailsClaimsEntityWidget = withRouter( _MemberDetailsClaimsEntityWidget );
